import { useEffect, useMemo, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DefaultButton } from "./DefaultButton";
import { generateTextShadow } from "@/lib/utils";

const POWER_USER_WALLETS = [
  // "EFigS9HSSgrskPpNFPv3uKpyMug4AQTvJ4aRpqbYTHi1", // @Mi1lerrr
  "3x2b1MZJAQGRiYriDrhVmYGSjeCnk3z7jHLfjg6mjLdu", // JONAS
  // "DeaikfzUkQYKV4WPNYhd6JyRorTWqG8VUhqHKcD7L9vb", // @LostFootCrypto
  // "AcQUsQJzKzrWn8hxGgToWZETa6AMqiG69Wuy8vJ2PT2F", // likoscm
  // "CFuN8AMiCAKgu4Xo9WTCGSkatXDQ5oZHgxsaCKn82Wvo", // mimakazo.skr
  "E721JhdCVK4B2Z1ZtEXjdSxxgYXRQobhLCwTLyKgXcsM",
  // "4DKPXChHE4pb2mxYeLiWinFN6jERaV9K9m5k5fEuJanX", // @cryptodripHQ
  "BeuTuuNKVKcsfX77MgPJA8jUjWb45ccQEncjrRUhPH3s",
  "AqPcAGv9kWyufLjg6epE7fXHrwC1JLv1tLaJu1PoLk2U",
  "A9Zb4zDUXw1UaVbqbNECDu7dQjc1esDeDy4qa9VKk8ru",
  // "25AHqZAtU2iZo8RXG3KAzrWTPpK319vcpxBJEuEwpdQt", // soldegenmonke
  "6BxuApodw2BwA3TFsJhyr48DSSwcicSMQmTGhdjp9UJ9",
  // "6bvFVAgf3hoAC1cKNh31sTkQEK3zH2s5ifk5r7ziWGp5", // whiskytitan.skr
  "Ax3e4CARH8vgT6F1hXJiSkgcvmgPN7LFH6GvFvqBx8Jp",
  "7ti2eBBUKG67UwS48Kh2h9kWyA5sCiPjpGrFdqN8M7nu",
  // "FFivJTqQexuaV31H8NyrTp1YvzW87NonkWc3qqaPCEZ5", // lizard.skr
  // "9o7SULSLp8CQSjHfLnzFMsKaCsa5Kza2xheFLMMpTuVh", // @Giraffeneck2
  // "4hv11Ne6m281fQVymAEji6eqXvpVVo3tsjsFMovhDo2F", // ooze.skr
  // "2cM3xLM2i5xk6hmyHfBaiDsTfLtWEMRFBEbw3ZSpg7Cz", // longtse1993.skr @kaka6710
  // "AYv5Je9xtM7rboXnwckYVBHjhXpenDDw1Y7FYdQM8tQv", // weaver33.skr
  // "AoBtRYnpZCX5uwB46hzeGYfJFbMrReGm8D1P8rVttYyZ", // skrphone.skr
  "GewutBYKk4U5KayHKZhDLSmfH5g4omdjmqW92KwnFXW7", //
  "BxftW8VqSqJG8jJikXAW7itGJD158WyZ4vtw7Mx5cqN5", // Akram
  "GXCvFi8jMCcqk2NzdSqowJBJ6i5L6QQBuc2sgR54cmyQ", // rick.skr
] as const;

const POWER_USER_WALLET_SET = new Set<string>(POWER_USER_WALLETS);

const buildMessage = (walletAddress: string) =>
  `Hey! I am your user ${walletAddress}! really excited to be part of the Legends!!`;

const TELEGRAM_BASE_URL = "https://t.me/jonassunandar?text=";
const DISMISS_STORAGE_KEY = "cfl:power-user-dialog-dismissed-at";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

export function PowerUserDialog() {
  const { ready, authenticated, user } = usePrivy();
  const walletAddress = user?.wallet?.address ?? "";
  const isPowerUser =
    ready &&
    authenticated &&
    walletAddress.length > 0 &&
    POWER_USER_WALLET_SET.has(walletAddress);

  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const storedDismissedAt =
        window.localStorage.getItem(DISMISS_STORAGE_KEY);
      if (!storedDismissedAt) {
        setDismissedAt(null);
        return;
      }

      const parsedDismissedAt = Number(storedDismissedAt);
      if (Number.isNaN(parsedDismissedAt)) {
        setDismissedAt(null);
        return;
      }

      if (Date.now() - parsedDismissedAt >= DISMISS_DURATION_MS) {
        window.localStorage.removeItem(DISMISS_STORAGE_KEY);
        setDismissedAt(null);
        return;
      }

      setDismissedAt(parsedDismissedAt);
    } catch {
      setDismissedAt(null);
    }
  }, []);

  const isDismissed = useMemo(() => {
    if (dismissedAt === null) {
      return false;
    }

    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  }, [dismissedAt]);

  useEffect(() => {
    if (!isPowerUser) {
      setOpen(false);
      setHasOpened(false);
      return;
    }

    if (isDismissed) {
      setOpen(false);
      return;
    }

    if (!hasOpened) {
      setOpen(true);
      setHasOpened(true);
    }
  }, [hasOpened, isDismissed, isPowerUser]);

  const { telegramUrl, message } = useMemo(() => {
    if (!walletAddress) {
      return { telegramUrl: "", message: "" };
    }

    const fullMessage = buildMessage(walletAddress);
    const encodedMessage = encodeURIComponent(fullMessage);
    return {
      telegramUrl: `${TELEGRAM_BASE_URL}${encodedMessage}`,
      message: fullMessage,
    };
  }, [walletAddress]);

  const handleOpenTelegram = () => {
    if (!telegramUrl) {
      return;
    }

    if (typeof window !== "undefined") {
      window.open(telegramUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleMaybeLater = () => {
    const now = Date.now();

    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(DISMISS_STORAGE_KEY, String(now));
      } catch {
        // Ignore storage errors
      }
    }

    setDismissedAt(now);
    setOpen(false);
  };

  if (!isPowerUser) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogOverlay
        className="fixed inset-0 bg-[#1D2B53] z-100!"
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
      <DialogContent
        className="bg-[#1D2B53]! gap-6 border-0! shadow-none! z-100!"
        style={{
          borderImage: "none",
        }}
      >
        <DialogHeader className="space-y-2 text-center">
          <DialogTitle
            className="text-2xl font-semibold uppercase tracking-wide text-white text-center"
            style={{
              textShadow: generateTextShadow(1.5),
            }}
          >
            You are a certified CFL Legend!
          </DialogTitle>
          <DialogDescription className="text-base leading-relaxed text-white/90 text-center">
            You received this message because you are on of the Top 20 players.
            Get access to private channels, early alpha, and help shape the
            community.
          </DialogDescription>
        </DialogHeader>

        <div className="relative overflow-hidden dark-blue-container p-4 text-white">
          <p>👉 Contact our team member on Telegram to get your invite!</p>
        </div>

        <DialogFooter className="flex w-full flex-col-reverse sm:flex-row justify-between gap-3">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              className="flex-1 justify-center border-white/20 bg-white/5 text-white hover:border-white/50 hover:bg-white/10"
              onClick={handleMaybeLater}
            >
              Maybe later
            </Button>
          </DialogClose>
          <DefaultButton
            type="button"
            onClick={handleOpenTelegram}
            className="flex-1 justify-center uppercase h-10"
          >
            Contact Jonas on Telegram
          </DefaultButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
